import * as React from 'react';
import Alert from 'antd/lib/alert';
import Popover from 'antd/lib/popover';
import Space from 'antd/lib/space';
import Table from 'antd/lib/table';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { ColumnsType } from 'antd/lib/table';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
    AndroidDependenciesDeclarationSite,
    AndroidDependenciesDependencyReport,
    AndroidDependenciesReport,
} from '@/types/androidDependencies';
import AndroidDependencyDetailContent from '@/components/dependencies/AndroidDependencyDetailPopover';
import FilesUsedPopover from '@/components/dependencies/FilesUsedPopover';
import {
    RESOLUTION_LABELS,
    dependencyGraphChain,
    formatResolutionLabel,
    formatVersionStatusLabel,
    isCatalogOnly,
    isGradlePlugin,
    isIndirect,
    versionStatusTagColor,
} from '@/components/dependencies/androidDependenciesUtils';
import { antGrey, rwColors } from '@/components/styles/globalStyles';

const declarationLocation = (site: AndroidDependenciesDeclarationSite): string =>
    site.line ? `${site.file}:${site.line}` : site.file;

// A coordinate can be declared in several modules and be broken in only some of them, so the ones
// worth showing are the sites whose own version is what failed to parse.
const failingDeclarationSites = (row: AndroidDependenciesDependencyReport): AndroidDependenciesDeclarationSite[] => {
    const sites = row.declarations ?? [];
    const failing = sites.filter((site) => site.version === row.currentVersion);
    return failing.length > 0 ? failing : sites;
};

const versionStatusTooltip = (row: AndroidDependenciesDependencyReport): React.ReactNode => {
    if (row.versionManagedBy) {
        return `Version comes from ${row.versionManagedBy}`;
    }
    if (row.versionStatus === 'not-on-public-repository') {
        return 'Published to a private repository, so no latest version can be checked';
    }
    if (row.versionStatus === 'invalid-version') {
        const sites = failingDeclarationSites(row);
        return (
            <Space direction="vertical" size={4}>
                <Text style={{ color: 'inherit' }}>
                    Runway couldn&apos;t parse {sites.length > 1 ? 'these declarations' : 'this declaration'}, please
                    review {sites.length > 1 ? 'them' : 'it'}:
                </Text>
                {sites.map((site) => (
                    <Space key={declarationLocation(site)} direction="vertical" size={0}>
                        <Text style={{ color: 'inherit', fontSize: 12 }}>{declarationLocation(site)}</Text>
                        {site.declaration && (
                            <Text code style={{ fontSize: 12, wordBreak: 'break-all' }}>
                                {site.declaration}
                            </Text>
                        )}
                    </Space>
                ))}
            </Space>
        );
    }
    return undefined;
};

const makeDependencyColumns = (hasLockfiles: boolean): ColumnsType<AndroidDependenciesDependencyReport> => [
    {
        title: 'Dependency',
        dataIndex: 'coordinate',
        key: 'coordinate',
        width: 420,
        fixed: 'left',
        ellipsis: true,
        sorter: (a, b) => a.coordinate.localeCompare(b.coordinate),
        render: (coordinate: string, row) => {
            const chain = hasLockfiles ? dependencyGraphChain(row.dependencyGraph) : undefined;
            return (
                <Popover
                    content={<AndroidDependencyDetailContent row={row} />}
                    trigger="hover"
                    placement="right"
                    mouseEnterDelay={0.2}
                >
                    <Space direction="vertical" size={0} style={{ cursor: 'help', maxWidth: '100%' }}>
                        <Text strong ellipsis>
                            {row.artifact}
                            {chain && (
                                <Text style={{ fontWeight: 'normal', color: antGrey.grey6 }}>{` (${chain})`}</Text>
                            )}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                            {coordinate}
                        </Text>
                    </Space>
                </Popover>
            );
        },
    },
    {
        title: 'Source',
        key: 'source',
        width: 120,
        sorter: (a, b) => {
            const aIndirect = isIndirect(a);
            const bIndirect = isIndirect(b);
            if (aIndirect === bIndirect) return 0;
            return aIndirect ? 1 : -1;
        },
        filters: [
            { text: 'Direct', value: 'direct' },
            { text: 'Transitive', value: 'indirect' },
            { text: 'Catalog', value: 'catalog' },
            { text: 'Plugin', value: 'plugin' },
        ],
        onFilter: (value, row) => {
            if (value === 'plugin') return isGradlePlugin(row);
            if (value === 'catalog') return isCatalogOnly(row);
            if (value === 'indirect') return isIndirect(row);
            return !isIndirect(row) && !isCatalogOnly(row) && !isGradlePlugin(row);
        },
        render: (_, row) =>
            isGradlePlugin(row) ? <Tag color="blue">Plugin</Tag>
            : isCatalogOnly(row) ? <Tag color="purple">Catalog</Tag>
            : isIndirect(row) ? <Tag>Transitive</Tag>
            : <Tag color="magenta">Direct</Tag>,
    },
    {
        title: 'Resolution',
        dataIndex: 'status',
        key: 'status',
        width: 170,
        sorter: (a, b) => a.status.localeCompare(b.status),
        filters: Object.entries(RESOLUTION_LABELS).map(([value, text]) => ({ text, value })),
        onFilter: (value, row) => row.status === value,
        render: (value: string) => <Text type="secondary">{formatResolutionLabel(value)}</Text>,
    },
    {
        title: 'Status',
        dataIndex: 'versionStatus',
        key: 'versionStatus',
        width: 180,
        sorter: (a, b) => (a.versionStatus ?? '').localeCompare(b.versionStatus ?? ''),
        filters: [
            { text: 'Up to date', value: 'up-to-date' },
            { text: 'Update available', value: 'update-available' },
            { text: 'Ahead of latest', value: 'ahead-of-latest' },
            { text: 'Managed by BOM', value: 'managed-by-bom' },
            { text: 'Managed by plugin', value: 'managed-by-plugin' },
            { text: 'Dynamic version', value: 'dynamic-version' },
            { text: 'Pinned to a commit', value: 'pinned-to-commit' },
            { text: 'Not on a public repository', value: 'not-on-public-repository' },
            { text: 'Invalid version', value: 'invalid-version' },
            { text: 'No current version', value: 'no-current-version' },
            { text: 'Version check failed', value: 'version-check-failed' },
        ],
        onFilter: (value, row) => row.versionStatus === value,
        render: (value: string, row) => (
            <Space direction="vertical" size={0}>
                <Tooltip title={versionStatusTooltip(row)}>
                    <Tag color={versionStatusTagColor(value)}>{formatVersionStatusLabel(value)}</Tag>
                </Tooltip>
                {row.checkError && (
                    <Text type="danger" style={{ fontSize: 12 }}>
                        {row.checkError}
                    </Text>
                )}
            </Space>
        ),
    },
    {
        title: 'Current',
        dataIndex: 'currentVersion',
        key: 'currentVersion',
        width: 130,
        render: (value: string) => value || '-',
    },
    {
        title: 'Latest',
        dataIndex: 'latestVersion',
        key: 'latestVersion',
        width: 130,
        render: (value: string, row) => {
            if (!value) return '-';
            if (!row.latestIsStale) return value;
            return (
                <Tooltip title="The registry rate limited the last check — showing the previously found version">
                    <Text type="secondary">{value} *</Text>
                </Tooltip>
            );
        },
    },
    {
        title: 'Latest pre-release',
        dataIndex: 'latestPreReleaseVersion',
        key: 'latestPreReleaseVersion',
        width: 150,
        render: (value: string) => value || '-',
    },
];

const AndroidDependenciesPanelBody: React.FC<{
    destinationId: string;
    report: AndroidDependenciesReport;
}> = ({ destinationId, report }) => {
    const files: { label: string; path: string }[] = [];
    for (const path of report.buildGradlePaths ?? []) {
        if (path) files.push({ label: 'build.gradle', path });
    }
    for (const path of report.gradleLockfilePaths ?? []) {
        if (path) files.push({ label: 'Gradle lockfile', path });
    }
    for (const path of report.versionCatalogPaths ?? []) {
        if (path) files.push({ label: 'Version catalog', path });
    }
    for (const path of report.gradlePropertiesPaths ?? []) {
        if (path) files.push({ label: 'gradle.properties', path });
    }
    for (const path of report.buildSrcPaths ?? []) {
        if (path) files.push({ label: 'buildSrc', path });
    }

    const hasLockfiles = (report.gradleLockfilePaths ?? []).length > 0;
    const hasVersionCatalogs = (report.versionCatalogPaths ?? []).length > 0;
    const unresolvedAccessors = report.unresolvedCatalogAccessors ?? [];

    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Space size={6}>
                <Popover
                    content={<FilesUsedPopover files={files} />}
                    trigger="hover"
                    placement="right"
                    mouseEnterDelay={0.2}
                >
                    <InfoCircleOutlined style={{ color: rwColors.primary, cursor: 'help' }} />
                </Popover>
                <Text type="secondary">
                    {hasLockfiles ?
                        'Resolved from Gradle lockfiles'
                    : hasVersionCatalogs ?
                        'No Gradle lockfile configured — listing what the version catalog declares plus the dependencies declared in the scanned build files'
                    :   'No Gradle lockfile configured — only dependencies declared in the scanned build files are listed'
                    }
                </Text>
            </Space>
            {unresolvedAccessors.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    message={`${unresolvedAccessors.length} version catalog ${unresolvedAccessors.length === 1 ? 'reference is' : 'references are'} unresolved and missing from this report`}
                    description={`Add the version catalog that defines ${unresolvedAccessors.slice(0, 5).join(', ')}${unresolvedAccessors.length > 5 ? ', …' : ''} to the configuration.`}
                />
            )}
            <Table
                size="middle"
                rowKey={(row) => `${destinationId}-${row.coordinate}`}
                columns={makeDependencyColumns(hasLockfiles)}
                dataSource={report.dependencies}
                scroll={{ x: 'max-content' }}
                pagination={{
                    defaultPageSize: 25,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '25', '50', '100', '200'],
                    showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
                }}
            />
        </Space>
    );
};

export default AndroidDependenciesPanelBody;
